import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../lib/supabaseClient";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");

      // If user came to /login directly, never redirect.
      if (!next) return;

      const { data, error } = await supabase.auth.getUser();
      if (cancelled) return;

      if (!error && data?.user) {
        navigate(next, { replace: true });
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(cleanEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!password) {
      setError("Password is required");
      return;
    }

    setLoading(true);

    try {
      const next =
        new URLSearchParams(window.location.search).get("next") || "/dashboard";

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      if (!data?.user) {
        setError("Login did not complete. Please try again.");
        setLoading(false);
        return;
      }

      // ✅ Member → auth linking happens on every successful login
      try {
        await supabase.functions.invoke("member-link-auth", { body: {} });
      } catch {
        // non-fatal; continue login flow
      }

      navigate(next, { replace: true });
    } catch {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-white px-6 py-10 sm:py-14 flex items-center justify-center"
      style={{
        paddingTop: "max(2.5rem, env(safe-area-inset-top))",
        paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-6xl md:text-7xl font-semibold tracking-tight mb-6 pb-2 logo-gradient">
            Niyyah
          </h1>
          <p className="text-sm text-niyyah-muted font-light mb-2">
            Welcome back
          </p>
          <p className="text-xs text-niyyah-muted/70 font-light max-w-sm mx-auto">
            Sign in to continue your journey
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-xs text-niyyah-muted font-medium mb-2 uppercase tracking-wider"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              placeholder="you@example.com"
              className="w-full p-4 text-sm text-niyyah-text font-light bg-white border border-gray-200 rounded-xl focus:border-niyyah-light focus:ring-1 focus:ring-niyyah-light/20 outline-none placeholder:text-gray-400 transition-all"
              disabled={loading}
            />
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block text-xs text-niyyah-muted font-medium mb-2 uppercase tracking-wider"
            >
              Password
            </label>

            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              placeholder="••••••••"
              className="w-full p-4 text-sm text-niyyah-text font-light bg-white border border-gray-200 rounded-xl focus:border-niyyah-light focus:ring-1 focus:ring-niyyah-light/20 outline-none placeholder:text-gray-400 transition-all"
              disabled={loading}
            />
          </div>

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="text-xs text-red-500 font-light text-center"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Circular Buttons */}
          <div className="flex justify-center gap-6 pt-6">
            <button
              type="submit"
              disabled={loading}
              className="auth-button-circle"
            >
              <span className="text-sm font-light leading-tight relative z-10 text-center">
                {loading ? "Signing in..." : "Login"}
              </span>
            </button>

            <Link
              to="/signup"
              className={`auth-button-circle-secondary ${
                loading ? "pointer-events-none" : ""
              }`}
            >
              <span className="text-sm font-light leading-tight relative z-10 text-center">
                Sign Up
              </span>
            </Link>
          </div>
        </motion.form>

        {/* Back link */}
        <div className="text-center mt-10 sm:mt-12">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-niyyah-muted hover:text-niyyah-purple transition-colors font-light inline-flex items-center gap-1"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to homepage
          </button>
        </div>
      </motion.div>

      <style>{`
        .logo-gradient {
          background-image:
            url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
            linear-gradient(
              135deg,
              #7a6ba8 0%,
              #8b7db8 12%,
              #9b8fc4 25%,
              #8c9ed8 38%,
              #7ba5d9 50%,
              #6bc4e0 62%,
              #b89bc8 75%,
              #9b8fc4 88%,
              #7a6ba8 100%
            );
          background-size:
            200px 200px,
            400% 400%;
          background-blend-mode: overlay, normal;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: logoGradientFluctuate 12s ease-in-out infinite;
        }

        @keyframes logoGradientFluctuate {
          0%,
          100% {
            background-position:
              0px 0px,
              0% 50%;
          }
          25% {
            background-position:
              0px 0px,
              100% 50%;
          }
          50% {
            background-position:
              0px 0px,
              50% 100%;
          }
          75% {
            background-position:
              0px 0px,
              0% 100%;
          }
        }

        .auth-button-circle {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: transform 0.3s ease;
          overflow: hidden;
          background: #9b8fc4;
          border: none;
          cursor: pointer;
          text-decoration: none;
        }

        .auth-button-circle::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.5;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .auth-button-circle:hover:not(:disabled) {
          transform: scale(1.05);
        }

        .auth-button-circle:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auth-button-circle-secondary {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9b87c7;
          transition: all 0.3s ease;
          overflow: hidden;
          background: #f3f0f9;
          border: none;
          cursor: pointer;
          text-decoration: none;
        }

        .auth-button-circle-secondary::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.3;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .auth-button-circle-secondary:hover {
          transform: scale(1.05);
          background: #ebe5f5;
        }
      `}</style>
    </div>
  );
}